import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, merge, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, filter, switchMap, catchError, map, finalize } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { UserService } from '../../services/user.service';
import { COMPANY_SEARCH_LIMIT, isCompanySuspended } from '../../utils/company-search';

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
<div class="search-container">
  <div class="search-input-group" [class.focused]="isFocused">
    <mat-icon class="search-icon">search</mat-icon>
    <input
      type="text"
      [formControl]="globalSearchControl"
      [matAutocomplete]="globalAuto"
      placeholder="Buscar empresa o usuario..."
      (focus)="isFocused = true"
      (blur)="isFocused = false"
      class="ds-search-input"
      autocomplete="off"
    >
    <div class="spinner-wrapper" *ngIf="globalSearchLoading">
      <mat-spinner diameter="18"></mat-spinner>
    </div>
  </div>

  <mat-autocomplete
    #globalAuto="matAutocomplete"
    [displayWith]="displaySearchResult"
    (optionSelected)="onSearchResultSelected($event)"
    class="ds-autocomplete-panel"
  >
    <mat-option
      *ngIf="showSearchPanel"
      class="global-search-panel-anchor"
      disabled
      [value]="panelAnchor"
    ></mat-option>
    <div class="search-tabs" (mousedown)="$event.preventDefault(); $event.stopPropagation();">
      <button type="button" class="tab-item" [class.active]="searchMode === 'company'" (click)="setSearchMode('company')">
        <mat-icon>business</mat-icon> 
        <span>Empresas</span>
        <span class="count" *ngIf="companySearchResults.length">{{companySearchResults.length}}</span>
      </button>
      <button type="button" class="tab-item" [class.active]="searchMode === 'user'" (click)="setSearchMode('user')">
        <mat-icon>person</mat-icon> 
        <span>Usuarios</span>
        <span class="count" *ngIf="userSearchResults.length">{{userSearchResults.length}}</span>
      </button>
    </div>

    <div class="results-container">
        <ng-container *ngIf="searchMode === 'company'">
            <mat-option *ngFor="let company of companySearchResults" [value]="{type: 'company', data: company}" class="ds-option">
                <div class="option-row">
                    <div class="option-avatar bg-blue-light">
                        <mat-icon>business</mat-icon>
                    </div>
                    <div class="option-info">
                        <span class="option-title">
                            {{ company.nombre }}
                            <span class="badge-suspended" *ngIf="company.suspended">Suspendida</span>
                        </span>
                        <span class="option-subtitle">
                            ID: #{{ company.id }}
                            <span *ngIf="company.biRazonSocial"> · Razón social: {{ company.biRazonSocial }}</span>
                            <span *ngIf="company.holdingId"> · Holding: {{ company.holdingId }}</span>
                        </span>
                    </div>
                </div>
            </mat-option>
            <div class="no-results" *ngIf="!globalSearchLoading && companySearchResults.length === 0 && (globalSearchControl.value?.length || 0) > 1">
                <p>No se encontraron empresas</p>
            </div>
        </ng-container>

        <ng-container *ngIf="searchMode === 'user'">
            <mat-option *ngFor="let user of userSearchResults" [value]="{type: 'user', data: user}" class="ds-option">
                <div class="option-row">
                    <div class="option-avatar bg-emerald-light">
                        <mat-icon>person</mat-icon>
                    </div>
                    <div class="option-info">
                        <span class="option-title">{{ user.nombre_completo || user.username }}</span>
                        <span class="option-subtitle">
                            <ng-container *ngIf="user.nombre_completo">{{ user.username }} · </ng-container>#{{ user.id }}
                            <span *ngIf="user.identificacion"> | {{ user.identificacion }}</span>
                        </span>
                    </div>
                </div>
            </mat-option>
            <div class="no-results" *ngIf="!globalSearchLoading && userSearchResults.length === 0 && (globalSearchControl.value?.length || 0) > 1">
                <p>No se encontraron usuarios</p>
            </div>
        </ng-container>
    </div>
  </mat-autocomplete>
</div>
  `,
  styles: [`
    .search-container { width: 100%; max-width: 500px; position: relative; }
    
    .search-input-group {
      display: flex; align-items: center; background: rgba(255, 255, 255, 0.15); 
      border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px;
      padding: 0 16px; height: 40px; transition: all .2s cubic-bezier(0.4, 0, 0.2, 1);
      backdrop-filter: blur(8px);
    }
    .search-input-group.focused { 
      background: #fff; border-color: #fff; 
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1);
      transform: translateY(-1px);
    }
    
    .search-icon { color: rgba(255,255,255,0.8); font-size: 20px; width: 20px; height: 20px; margin-right: 12px; display: flex; align-items: center; justify-content: center; }
    .focused .search-icon { color: #066bf1; }
    
    .ds-search-input {
      flex: 1; background: transparent; border: none; color: #fff; 
      font-size: 14px; outline: none; width: 100%;
    }
    .focused .ds-search-input { color: #1e293b; }
    .ds-search-input::placeholder { color: rgba(255,255,255,0.6); }
    .focused .ds-search-input::placeholder { color: #94a3b8; }
    
    .spinner-wrapper { display: flex; align-items: center; margin-left: 8px; }
    .focused ::ng-deep .mat-mdc-progress-spinner circle { stroke: #066bf1 !important; }

    /* Autocomplete Panel Customization
       Keep overflow:hidden for rounded corners, but scroll inside .results-container
       so long company/user lists remain reachable. */
    ::ng-deep .ds-autocomplete-panel.mat-mdc-autocomplete-panel {
      background: #fff !important; border-radius: 16px !important; margin-top: 12px !important;
      box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04) !important;
      border: 1px solid #e2e8f0 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
      max-height: min(70vh, 480px) !important;
    }

    ::ng-deep .ds-autocomplete-panel .search-tabs {
      display: flex; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
      flex-shrink: 0;
    }
    ::ng-deep .ds-autocomplete-panel .results-container {
      overflow-y: auto !important;
      flex: 1 1 auto;
      min-height: 0;
      -webkit-overflow-scrolling: touch;
    }
    .tab-item {
      flex: 1; border: none; background: transparent; padding: 12px; cursor: pointer;
      display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 13px; font-weight: 600;
      color: #64748b; border-bottom: 2px solid transparent; transition: all .2s;
    }
    .tab-item:hover { color: #066bf1; background: #eff6ff; }
    .tab-item.active { color: #066bf1; border-bottom-color: #066bf1; background: #fff; }
    .tab-item mat-icon { font-size: 18px; width: 18px; height: 18px; }
    .tab-item .count { background: #e2e8f0; color: #475569; font-size: 11px; padding: 1px 6px; border-radius: 10px; }
    .tab-item.active .count { background: #dbeafe; color: #1e40af; }

    .ds-option { height: auto !important; padding: 8px 16px !important; line-height: 1.2 !important; }
    .ds-option:hover { background: #f8fafc !important; }
    
    .option-row { display: flex; align-items: center; gap: 12px; }
    .option-avatar { 
        width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
        mat-icon { font-size: 18px; width: 18px; height: 18px; }
    }
    .bg-blue-light { background: #eff6ff; color: #066bf1; }
    .bg-emerald-light { background: #ecfdf5; color: #10b981; }
    
    .option-info { display: flex; flex-direction: column; gap: 2px; }
    .option-title { font-size: 14px; font-weight: 600; color: #1e293b; display: flex; align-items: center; gap: 8px; }
    .option-subtitle { font-size: 12px; color: #64748b; }
    .badge-suspended { background: #fee2e2; color: #b91c1c; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 8px; text-transform: uppercase; letter-spacing: .02em; }

    .no-results { padding: 24px; text-align: center; color: #94a3b8; font-size: 13px; }

    ::ng-deep .global-search-panel-anchor.mat-mdc-option {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
    }
  `]
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  static readonly RESULT_LIMIT = 50;
  static readonly COMPANY_RESULT_LIMIT = COMPANY_SEARCH_LIMIT;

  readonly panelAnchor = { type: 'panel-anchor' as const };
  private lastSearchQuery = '';

  globalSearchControl = new FormControl('');
  companySearchResults: any[] = [];
  userSearchResults: any[] = [];
  globalSearchLoading = false;
  searchMode: 'company' | 'user' = 'company';
  isFocused = false;

  private destroy$ = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private userService: UserService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.globalSearchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      filter((value): value is string => typeof value === 'string'),
      switchMap((value: string) => {
        this.lastSearchQuery = value;
        if (!value || value.length < 2) {
          this.companySearchResults = [];
          this.userSearchResults = [];
          this.globalSearchLoading = false;
          return of(null);
        }
        this.globalSearchLoading = true;
        const companies$ = this.companyService.getCompaniesPaginated({
          limit: GlobalSearchComponent.COMPANY_RESULT_LIMIT,
          search: value,
        }).pipe(
          map((companies) => ({ kind: 'companies' as const, payload: companies })),
          catchError(() => of({ kind: 'companies' as const, payload: { companies: [] } })),
        );
        const users$ = this.userService.getUserList({
          page: 1,
          limit: GlobalSearchComponent.RESULT_LIMIT,
          userSearch: value,
        }).pipe(
          map((users) => ({ kind: 'users' as const, payload: users })),
          catchError(() => of({ kind: 'users' as const, payload: { data: [] } })),
        );
        // merge (not forkJoin): each side paints as soon as it returns, and
        // switchMap drops the previous pair when the query changes.
        return merge(companies$, users$).pipe(
          finalize(() => {
            this.globalSearchLoading = false;
          }),
        );
      })
    ).subscribe((event) => {
      if (!event) return;
      if (event.kind === 'companies') {
        this.companySearchResults = this.mapCompanies(event.payload);
      } else {
        this.userSearchResults = this.mapUsers(event.payload);
      }
      this.updateSearchMode(this.lastSearchQuery);
    });
  }

  get showSearchPanel(): boolean {
    const value = this.globalSearchControl.value;
    return typeof value === 'string' && value.length > 1;
  }

  private looksLikeUserSearchQuery(query: string): boolean {
    const q = query.trim();
    if (!q) {
      return false;
    }
    if (q.includes('@')) {
      return true;
    }
    return q.includes('.') && !/\s/.test(q);
  }

  private updateSearchMode(query: string): void {
    if (this.userSearchResults.length > 0 && this.companySearchResults.length === 0) {
      this.searchMode = 'user';
      return;
    }
    if (this.companySearchResults.length > 0 && this.userSearchResults.length === 0) {
      this.searchMode = 'company';
      return;
    }
    if (this.userSearchResults.length > 0 && this.looksLikeUserSearchQuery(query)) {
      this.searchMode = 'user';
    }
  }

  private mapCompanies(compRes: any): any[] {
    let compResults: any[] = [];
    if (compRes && Array.isArray(compRes)) compResults = compRes;
    else if (compRes?.companies && Array.isArray(compRes.companies)) compResults = compRes.companies;
    else if (compRes?.data && Array.isArray(compRes.data)) compResults = compRes.data;
    else if (compRes?.id) compResults = [compRes];

    return compResults
      .map((c: any) => {
        const suspended = isCompanySuspended(c);
        const biRazonSocial = (
          c.biRazonSocial ?? c.bi_razon_social ?? c.razonSocial ?? c.razon_social ?? ''
        ).toString().trim() || null;
        return {
          ...c,
          id: c.id || c.company_id || c.ID,
          nombre: c.name || c.nombre || c.company_name || c.nombre_empresa || 'Empresa sin nombre',
          suspended,
          biRazonSocial,
        };
      });
  }

  private mapUsers(usrRes: any): any[] {
    let usrResults: any[] = [];
    if (usrRes && Array.isArray(usrRes)) usrResults = usrRes;
    else if (usrRes?.data && Array.isArray(usrRes.data)) usrResults = usrRes.data;
    else if (usrRes?.items && Array.isArray(usrRes.items)) usrResults = usrRes.items;
    else if (usrRes?.id) usrResults = [usrRes];

    return usrResults.map((u: any) => {
      const firstname = (u.firstname ?? u.nombre ?? '').toString().trim();
      const lastname = (u.lastname ?? u.apellido ?? '').toString().trim();
      const fullname = (u.fullname ?? u.nombre_completo ?? '').toString().trim();
      const displayName = fullname || [firstname, lastname].filter(Boolean).join(' ');
      return {
        ...u,
        id: u.id,
        username: u.username,
        nombre_completo: displayName || null,
        identificacion: u.identification ?? u.identificacion ?? null,
      };
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSearchMode(mode: 'company' | 'user'): void {
    this.searchMode = mode;
  }

  displaySearchResult(result: any): string {
    if (!result?.data) return '';
    if (result.type === 'company') return `${result.data.nombre} (#${result.data.id})`;
    if (result.type === 'user') {
      const name = result.data.nombre_completo || result.data.username;
      return `${name} (#${result.data.id})`;
    }
    return '';
  }

  onSearchResultSelected(event: MatAutocompleteSelectedEvent): void {
    const result = event.option.value;
    if (result?.type === 'panel-anchor') {
      return;
    }
    if (!result?.data?.id) return;
    if (result.type === 'company') this.router.navigate(['/companies/edit', result.data.id]);
    else if (result.type === 'user') this.router.navigate(['/users/edit', result.data.id]);
    setTimeout(() => {
      this.globalSearchControl.setValue('');
      this.companySearchResults = [];
      this.userSearchResults = [];
    }, 100);
  }
}
